import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import SearchBox from '../components/SearchBox';
import SourcesPanel from '../components/SourcesPanel';
import EnhancedLoadingStatus from '../components/EnhancedLoadingStatus';
import AdvancedConceptCard from '../components/AdvancedConceptCard';
import { MarkdownContent } from '../components/MarkdownContent';
import { generateOverview } from '../services/llm';
import { searchFandom, SearchProvider, getSearchProvider, setSearchProvider } from '../services/searchService';
import { analyzeQueryIntent } from '../services/queryAnalyzerLLM';
import { LLMResponse } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { historyService } from '../services/historyService';
import { memoryService } from '../services/memoryService';
import { memoryLLMService, MemorySummary } from '../services/memoryLLMService';
// import { HistoryPanel } from '../components/HistoryPanel'; // Removed: Replaced by Your Journey sidebar
// import { MemoryIndicator } from '../components/MemoryIndicator'; // Removed: Memory shown only in loading
import { useNavigate, useLocation } from 'react-router-dom';
import { DebugInfo } from '../components/DebugInfo';
import { supabase } from '../lib/supabase';
// import { generateAdvancedTheme } from '../services/advancedThemeGenerator'; // DISABLED: Keep default theme
import { Leaderboard } from '../components/Leaderboard';
import { UsernameSetup } from '../components/UsernameSetup';
import { querySpaceService } from '../services/querySpaceService';
import { topicService } from '../services/topicService';
import LeftSidebar from '../components/LeftSidebar';
import RightSidebar from '../components/RightSidebar';
import PatternBasedRecommendations from '../components/PatternBasedRecommendations';
import { mixpanelService } from '../services/mixpanel';
import { activityTracker } from '../services/activityTracker';
import { rateLimiter } from '../services/rateLimiter';
import { RateLimitToast } from '../components/RateLimitToast';

const EmergencyNotice = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: #fbbf24;
  color: #92400e;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #f59e0b;
`;

const NoticeText = styled.div`
  flex: 1;
  text-align: center;
`;

const DismissButton = styled.button`
  background: none;
  border: none;
  color: #92400e;
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  
  &:hover {
    background: rgba(146, 64, 14, 0.1);
  }
`;

const AppContainer = styled.div<{ hasNotice?: boolean }>`
  min-height: 100vh;
  display: flex;
  background: linear-gradient(
    135deg,
    var(--color-background) 0%,
    rgba(var(--color-surface-rgb, 30, 41, 59), 0.8) 100%
  );
  position: relative;
  margin-top: ${props => props.hasNotice ? '49px' : '0'};
  
  &::before {
    content: '';
    position: fixed;
    top: ${props => props.hasNotice ? '49px' : '0'};
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      600px circle at 50% 50%,
      rgba(var(--color-primary-rgb, 99, 102, 241), 0.05),
      transparent 50%
    );
    pointer-events: none;
    z-index: 1;
  }
`;

const TopBar = styled.div<{ hasSearch?: boolean; hasNotice?: boolean }>`
  position: fixed;
  top: ${props => props.hasNotice ? '49px' : '0'};
  left: 0;
  right: 0;
  height: 60px;
  background: rgba(var(--color-surface-rgb, 30, 41, 59), 0.95);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(var(--color-border-rgb, 51, 65, 85), 0.3);
  box-shadow: var(--shadow-md);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gap-md);
  padding: 0 var(--gap-lg);
  z-index: 100;
  
  @media (max-width: 1024px) {
    left: 0;
    right: 0;
    ${props => props.hasSearch ? `
      flex-direction: column;
      height: auto;
      padding: var(--gap-md);
      gap: var(--gap-md);
    ` : ''}
  }
`;

const AppTitle = styled.h1`
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text);
  margin: 0;
  background: linear-gradient(135deg, var(--color-text) 0%, var(--color-primary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SearchContainer = styled.div`
  flex: 1;
  max-width: 400px;
  
  @media (max-width: 1024px) {
    max-width: 300px;
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  gap: var(--gap-md);
  flex-shrink: 0;
  
  @media (max-width: 1024px) {
    display: none; /* Hide individual buttons on mobile - use hamburger menus instead */
  }
`;

const MainContent = styled.div<{ hasHeaderSearch?: boolean; rightSidebarOpen?: boolean; hasNotice?: boolean }>`
  margin-left: 280px; /* Account for left sidebar on desktop */
  width: ${props => props.rightSidebarOpen ? 'calc(100vw - 280px - 320px)' : 'calc(100vw - 280px)'}; /* Dynamic width based on sidebars */
  margin-top: ${props => props.hasNotice ? '109px' : '60px'}; /* 60px topbar + 49px notice */
  padding: var(--gap-xl);
  min-height: ${props => props.hasNotice ? 'calc(100vh - 109px)' : 'calc(100vh - 60px)'};
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: width 0.3s ease; /* Smooth transition when right sidebar toggles */
  
  @media (max-width: 1024px) {
    margin-left: 0;
    width: 100%; /* Reset width on mobile */
    margin-top: ${props => {
      if (props.hasNotice && props.hasHeaderSearch) return '189px'; // 49 + 140
      if (props.hasNotice) return '109px'; // 49 + 60
      if (props.hasHeaderSearch) return '140px'; // 140
      return '60px'; // 60
    }};
    padding: var(--gap-md);
    min-height: ${props => {
      if (props.hasNotice && props.hasHeaderSearch) return 'calc(100vh - 189px)';
      if (props.hasNotice) return 'calc(100vh - 109px)';
      if (props.hasHeaderSearch) return 'calc(100vh - 140px)';
      return 'calc(100vh - 60px)';
    }};
  }
  
  @media (max-width: 768px) {
    padding: var(--gap-sm);
  }
`;

const SearchSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--gap-xl);
  max-width: 800px;
  margin: 0 auto;
`;

const MobileRecommendations = styled.div`
  width: 100%;
  max-width: 600px;
  display: none;
  
  @media (max-width: 1024px) {
    display: block;
  }
`;

const WelcomeTitle = styled.h2`
  font-size: clamp(32px, 4vw, 48px);
  color: var(--color-text);
  margin: 0;
  text-align: center;
  font-weight: var(--font-weight-medium);
  opacity: ${props => props.children ? '0' : '1'};
  transition: opacity 0.3s ease;
`;

const QueryContextBar = styled.div`
  position: sticky;
  top: 0;
  background: rgba(var(--color-surface-rgb, 30, 41, 59), 0.95);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(var(--color-border-rgb, 51, 65, 85), 0.3);
  border-radius: var(--radius-md);
  padding: var(--gap-md);
  margin-bottom: var(--gap-lg);
  box-shadow: var(--shadow-sm);
  z-index: 50;
  display: block;
`;

const QueryText = styled.div`
  color: var(--color-text);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  margin: 0;
  text-align: center;
`;

const QueryLabel = styled.div`
  color: var(--color-text-secondary);
  font-size: var(--font-size-xs);
  text-transform: uppercase;
  letter-spacing: var(--letter-spacing-wide);
  margin-bottom: 4px;
  text-align: center;
`;

const ContentArea = styled.div`
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--gap-xl);
`;

const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--gap-xl);
  width: 100%;
`;

const ConceptGrid = styled.div<{ columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.columns || 2}, 1fr);
  gap: 16px; /* 8px grid: 2 * 8 */
  width: 100%;
  max-width: 1000px;
  margin: 0 auto; /* Center the concept grid */
  opacity: 0;
  animation: fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: 0.2s;
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(16px); /* 8px grid: 2 * 8 */
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @media (max-width: 1024px) {
    gap: 12px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
    max-width: 100%;
  }
`;

const Header = styled.div`
  position: absolute;
  top: 24px; /* 8px grid: 3 * 8 */
  right: 24px; /* 8px grid: 3 * 8 */
  display: flex;
  gap: 16px; /* 8px grid: 2 * 8 */
  z-index: 100;
`;

const ProviderToggle = styled.div`
  position: absolute;
  top: 24px; /* 8px grid: 3 * 8 */
  left: 24px; /* 8px grid: 3 * 8 */
  display: flex;
  align-items: center;
  gap: 8px; /* 8px grid: 1 * 8 */
  z-index: 100;
  font-size: 14px;
  color: var(--color-text-secondary);
`;

const ProviderButton = styled.button<{ active: boolean }>`
  padding: 8px 16px; /* 8px grid: 1 * 8, 2 * 8 */
  background: ${props => props.active ? 'var(--color-primary)' : 'var(--color-surface)'};
  color: ${props => props.active ? 'white' : 'var(--color-text)'};
  border: 1px solid ${props => props.active ? 'var(--color-primary)' : 'var(--color-border)'};
  border-radius: var(--radius-md);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? 'var(--color-primary)' : 'var(--color-surface-hover)'};
    border-color: var(--color-primary);
  }
`;

const IconButton = styled.button`
  width: 44px;
  height: 44px;
  padding: 10px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: var(--color-surface-hover);
    border-color: var(--color-primary);
    transform: translateY(-1px);
  }
  
  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }
`;

const HistoryIconButton = styled.button`
  width: 44px;
  height: 44px;
  background: var(--color-surface);
  border: 1px solid rgba(var(--color-border-rgb, 51, 65, 85), 0.3);
  border-radius: var(--radius-md);
  color: var(--color-text);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  flex-shrink: 0;
  position: relative;
  padding: 0;
  
  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }
  
  &:hover {
    background: var(--color-surface-hover);
    border-color: var(--color-primary);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const LeaderboardIconButton = styled.button`
  width: 44px;
  height: 44px;
  padding: 10px;
  background: linear-gradient(135deg, var(--color-primary), var(--color-accent));
  border: none;
  border-radius: var(--radius-md);
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px var(--color-primary-shadow, rgba(99, 102, 241, 0.3));
  }
  
  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }
`;

const OverviewSection = styled.div`
  background: var(--color-surface);
  border: 1px solid rgba(var(--color-border-rgb, 51, 65, 85), 0.3);
  border-radius: var(--radius-lg);
  padding: 32px; /* 8px grid: 4 * 8 */
  margin: 0 auto; /* Center the overview section */
  width: 100%;
  max-width: 800px; /* 8px grid: 100 * 8 */
  box-shadow: var(--shadow-lg);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      135deg,
      rgba(var(--color-primary-rgb, 99, 102, 241), 0.05) 0%,
      transparent 50%
    );
    border-radius: var(--radius-lg);
    pointer-events: none;
  }
`;

const SectionHeading = styled.h2`
  font-size: 24px; /* 8px grid: 3 * 8 */
  font-weight: 600;
  color: var(--color-text);
  margin: 0; /* Margin handled by parent gap */
  text-align: center;
`;

const MobileMenuButton = styled.button`
  display: none;
  width: 44px;
  height: 44px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text);
  cursor: pointer;
  transition: all 0.2s;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 1024px) {
    display: flex;
  }
  
  &:hover {
    background: var(--color-surface-hover);
    border-color: var(--color-primary);
    transform: translateY(-1px);
  }
  
  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }
`;

const MobileBackdrop = styled.div<{ show: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 15;
  opacity: ${props => props.show ? 1 : 0};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  
  @media (min-width: 1025px) {
    display: none;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalBackdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  position: relative;
  z-index: 1001;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease-out;
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [llmResponse, setLlmResponse] = useState<LLMResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentQuery, setCurrentQuery] = useState('');
  const [showNotice, setShowNotice] = useState(() => {
    // Show notice by default, but can be disabled via localStorage
    // To hide the notice, set localStorage.setItem('showSearchNotice', 'false')
    return localStorage.getItem('showSearchNotice') !== 'false';
  });
  const [memorySummary, setMemorySummary] = useState<MemorySummary | null>(null);
  const [searchProvider, setSearchProviderState] = useState<SearchProvider>('perplexity');
  const [lastError, setLastError] = useState<any>(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [needsUsername, setNeedsUsername] = useState(false);
  const [conceptTrigger, setConceptTrigger] = useState(0); // Trigger for concept component updates
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(false);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);
  const [clickedConceptIndex, setClickedConceptIndex] = useState<number | null>(null);
  const [rateLimitToast, setRateLimitToast] = useState<{
    message: string;
    remainingTime: number;
  } | null>(null);
  
  // Debug user state
  // console.log('HomePage user state:', user ? `Logged in as ${user.email}` : 'Not logged in');

  // Check if user needs to set username
  useEffect(() => {
    const checkUsername = async () => {
      if (!user) return;
      
      // Check if user signed in with Google and needs username
      const provider = user.app_metadata?.provider;
      if (provider === 'google') {
        const { data } = await supabase
          .from('user_data')
          .select('username')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (!data?.username) {
          setNeedsUsername(true);
        }
      } else if (provider === 'discord') {
        // Discord users automatically get their Discord username
        // console.log('Discord user metadata:', user.user_metadata);
        
        const discordUsername = user.user_metadata?.username ||        // Discord username
                               user.user_metadata?.global_name ||      // Discord display name  
                               user.user_metadata?.full_name || 
                               user.user_metadata?.name ||
                               user.user_metadata?.preferred_username ||
                               user.email?.split('@')[0];              // Fallback to email prefix
        
        // console.log('Extracted Discord username:', discordUsername);
        
        if (discordUsername) {
          // Save Discord username to database
          // console.log('Saving Discord username to database:', discordUsername);
          const { error } = await supabase
            .from('user_data')
            .upsert({
              user_id: user.id,
              username: discordUsername,
              display_name: discordUsername,
              updated_at: new Date().toISOString()
            }, {
              onConflict: 'user_id'
            });
          
          if (error) {
            // console.error('Error saving Discord username:', error);
          } else {
            // console.log('Successfully saved Discord username to database');
          }
        } else {
          // console.log('No Discord username found in metadata');
        }
      }
    };
    
    checkUsername();
  }, [user]);

  // Handle URL query parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const queryParam = searchParams.get('q');
    
    if (queryParam && queryParam !== currentQuery) {
      // Trigger search if we have a query parameter
      handleSearch(queryParam);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProviderChange = (provider: SearchProvider) => {
    setSearchProviderState(provider);
    setSearchProvider(provider); // Save to localStorage
  };

  const handleSearch = async (query: string) => {
    // Check rate limit first - prevent expensive backend calls
    const rateLimitCheck = rateLimiter.canMakeRequest('search');
    if (!rateLimitCheck.allowed) {
      setRateLimitToast({
        message: rateLimitCheck.message || 'Please wait before searching again',
        remainingTime: rateLimitCheck.remainingCooldown
      });
      return;
    }

    try {
      setIsLoading(true);
      setCurrentQuery(query);
      setMemorySummary(null);
      setClickedConceptIndex(null); // Reset clicked state

      // Record the search request
      rateLimiter.recordRequest('search');
      
      // Update RabbitHole stats for leaderboard (after rate limit check passes)
      await memoryService.updateRabbitHoleStats('search');

      // Check auth status
      const { data: { session } } = await supabase.auth.getSession();
      // console.log('Auth session:', session ? 'Authenticated' : 'Not authenticated');
      // console.log('User:', session?.user?.email);

      // NEW: Query Space deduplication
      const canonicalResult = await querySpaceService.getCanonicalQuery(query);
      const canonicalQuery = canonicalResult.canonical;
      
      // console.log('🔄 Query Deduplication:', {
      //   original: query,
      //   canonical: canonicalQuery,
      //   is_new: canonicalResult.is_new,
      //   frequency: canonicalResult.frequency,
      //   similar_queries: canonicalResult.similar_queries
      // });

      // Generate memory summary (use original query for personalization)
      const memoryPromise = memoryLLMService.generateMemorySummary(query);
      memoryPromise.then(summary => {
        if (summary) {
          setMemorySummary(summary);
        }
      });

      // Analyze query intent (use canonical query for consistency)
      const intent = await analyzeQueryIntent(canonicalQuery);
      // console.log('Query intent:', intent);

      // Generate dynamic theme based on query (use canonical for consistency)
      // DISABLED: Keep default theme always
      // try {
      //   await generateAdvancedTheme(canonicalQuery, intent);
      // } catch (error) {
      //   console.warn('Theme generation failed, using default:', error);
      // }

      // Search (use canonical query for efficient search)
      const searchResult = await searchFandom(canonicalQuery, intent, searchProvider);
      // console.log('Search result from edge function:', searchResult);
      const sources = searchResult.sources || [];
      // console.log('Sources extracted:', sources);
      // console.log('Sources length:', sources.length);
      setSearchResults(sources);

      // Record manual activity for accurate time tracking
      activityTracker.recordActivity('search');

      // Track search event (server-only for reliable business metrics)
      mixpanelService.track('search_performed', {
        query: query, // Original query for UX insights
        canonical_query: canonicalQuery, // Canonical for analytics consistency
        intent: intent.type,
        search_provider: searchProvider,
        results_count: sources.length,
        is_new_query: canonicalResult.is_new,
        query_frequency: canonicalResult.frequency,
        user_id: user?.id || 'anonymous',
        has_memory: !!memoryService
      });

      // Generate overview (use canonical query for consistency, but show original in UI)
      const overview = await generateOverview(canonicalQuery, intent, sources);
      setLlmResponse(overview);

      // NEW: Process topics from canonical query for Topic Space
      // (DISABLED to improve performance and reduce API costs - not needed for core functionality)
      // if (user && canonicalQuery) {
      //   try {
      //     console.log('🔄 Processing topics for user:', user.id, 'query:', canonicalQuery);
      //     const result = await topicService.processTopics({
      //       canonical_query: canonicalQuery,
      //       user_id: user.id
      //     });
      //     
      //     console.log('🎯 Topic processing result:', result);
      //     
      //     // Trigger topic component updates
      //     setConceptTrigger(prev => prev + 1);
      //     
      //     console.log('✅ Topics processed for Topic Space from canonical query:', canonicalQuery);
      //   } catch (error) {
      //     console.error('❌ Topic processing failed:', error);
      //     console.error('Error details:', error instanceof Error ? error.message : String(error));
      //   }
      // } else {
      //   console.log('⚠️ Skipping topic processing - user:', !!user, 'canonicalQuery:', canonicalQuery);
      // }

      // Save to history
      if (user) {
        try {
          const explorationData = {
            query: query, // Save original query for user context
            snapshot: {
              intent,
              searchResults: sources,
              llmResponse: overview,
              canonicalQuery: canonicalQuery, // Also save canonical for debugging
              deduplicationInfo: canonicalResult,
              timestamp: new Date().toISOString()
            }
          };
          
          await historyService.saveExploration(explorationData);
          await memoryService.updateMemoryFromExploration(
            canonicalQuery, // Use canonical for memory building
            overview.concepts,
            intent
          );
        } catch (saveError) {
          // console.error('Error saving exploration:', saveError);
          // Don't crash the app if saving fails
        }
      }

    } catch (error) {
      // console.error('Search error:', error);
      setLastError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadExploration = async (id: string) => {
    try {
      const exploration = await historyService.getExploration(id);
      if (!exploration) {
        // console.error('Exploration not found');
        return;
      }

      const { query, snapshot } = exploration.data;
      
      setCurrentQuery(query);
      setLlmResponse(snapshot.llmResponse);
      setSearchResults(snapshot.searchResults);
      setIsLoading(false);
      
      // console.log('Loaded exploration:', query);
    } catch (error) {
      // console.error('Error loading exploration:', error);
    }
  };

  const handleDismissNotice = () => {
    setShowNotice(false);
    localStorage.setItem('showSearchNotice', 'false');
  };

  return (
    <>
      {/* Emergency Notice */}
      {showNotice && (
        <EmergencyNotice>
          <NoticeText>
            ⚠️ We're aware of search issues due to heavy traffic and are working to fix it.
          </NoticeText>
          <DismissButton onClick={handleDismissNotice} title="Dismiss">
            ×
          </DismissButton>
        </EmergencyNotice>
      )}
      
      <AppContainer hasNotice={showNotice}>
        {/* Mobile Backdrop */}
      <MobileBackdrop 
        show={leftSidebarOpen || rightSidebarOpen} 
        onClick={() => {
          setLeftSidebarOpen(false);
          setRightSidebarOpen(false);
        }}
      />
      
      {/* Left Sidebar - Trending & Recommendations */}
      <LeftSidebar 
        userId={user?.id}
        onExplore={(query) => {
          handleSearch(query);
          setLeftSidebarOpen(false); // Close sidebar after search
        }}
        trigger={conceptTrigger}
        isOpen={leftSidebarOpen}
        onClose={() => setLeftSidebarOpen(false)}
        onShowLeaderboard={() => setShowLeaderboard(true)}
        onNavigateToGraph={() => navigate('/graph')}
        onOpenRightSidebar={() => setRightSidebarOpen(true)}
        hasHeaderSearch={!!(llmResponse || isLoading)}
      />
      
      {/* Right Sidebar - Exploration Journey */}
      {user && (
        <RightSidebar 
          userId={user.id}
          onExplore={(query) => {
            handleSearch(query); // Note: This will increment trending counts
            setRightSidebarOpen(false); // Close sidebar after search
          }}
          onLoadExploration={(id) => {
            handleLoadExploration(id); // Load saved exploration
            setRightSidebarOpen(false); // Close sidebar after loading
          }}
          trigger={conceptTrigger}
          isOpen={rightSidebarOpen}
          onClose={() => setRightSidebarOpen(false)}
          onBackToLeft={() => {
            setRightSidebarOpen(false);
            setLeftSidebarOpen(true);
          }}
          hasHeaderSearch={!!(llmResponse || isLoading)}
        />
      )}
      
      {/* Top Navigation Bar */}
      <TopBar hasSearch={!!(llmResponse || isLoading)} hasNotice={showNotice}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gap-md)', minWidth: 0 }}>
          {/* Mobile Menu Button - moved to left of title */}
          <MobileMenuButton 
            onClick={() => setLeftSidebarOpen(!leftSidebarOpen)}
            title="Recommendations Menu"
          >
            <svg viewBox="0 0 24 24">
              <path d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z"/>
            </svg>
          </MobileMenuButton>
          
          <AppTitle>Lore Spirals</AppTitle>
        </div>
        
        {(llmResponse || isLoading) && (
          <SearchContainer>
            <SearchBox onSearch={handleSearch} compact isLoading={isLoading} />
          </SearchContainer>
        )}
        
        <NavigationButtons>
          {user && (
            <>
              {/* History Icon - Your Journey */}
              <HistoryIconButton onClick={() => setRightSidebarOpen(!rightSidebarOpen)} title="Your exploration timeline and progress">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 115">
                  <path d="m53.5 34.207c-1.168 0-2.082 0.91797-2.082 2.082v15.207c0 0.54297 0.20703 1.082 0.625 1.457l12.418 12.418c0.41797 0.41797 0.95703 0.625 1.457 0.625s1.082-0.20703 1.457-0.625c0.83203-0.83203 0.83203-2.125 0-2.957l-11.832-11.832v-14.289c0.039062-1.125-0.91797-2.0859-2.043-2.0859z"/>
                  <path d="m53.375 15.875c-18.082 0-32.918 14.168-34 32l-3.25-3.707c-0.75-0.875-2.082-0.95703-2.957-0.20703s-0.95703 2.082-0.20703 2.957l6.957 8.043c0.41797 0.45703 0.95703 0.70703 1.582 0.70703s1.168-0.25 1.582-0.70703l6.957-8.043c0.75-0.875 0.66797-2.168-0.20703-2.957-0.875-0.75-2.168-0.66797-2.957 0.20703l-3.418 3.957c1.043-15.668 14.043-28.082 29.918-28.082 16.543 0 29.957 13.457 29.957 29.957s-13.457 29.957-29.957 29.957c-10.582 0-20.168-5.418-25.625-14.457-0.58203-1-1.875-1.293-2.875-0.70703-0.95703 0.58203-1.293 1.875-0.70703 2.875 6.25 10.293 17.168 16.457 29.207 16.457 18.832 0 34.125-15.293 34.125-34.125s-15.293-34.125-34.125-34.125z"/>
                </svg>
              </HistoryIconButton>
              
              <LeaderboardIconButton onClick={() => setShowLeaderboard(true)} title="Leaderboard">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
                  <path d="m22.328 3.4102c-0.74609 0.003906-1.3672 0.5625-1.4531 1.3008-0.26562 2.4023-0.44531 4.7305-0.54688 6.9922h-15.914c-0.44922 0-0.875 0.20312-1.1562 0.55859s-0.38281 0.81641-0.27734 1.2578l5.0039 20.668c2.1328 8.8125 8.5938 15.941 17.152 18.934l7.2227 2.5273c4.4609 6.1602 9.957 11.516 16.176 16.371v5.875l-17.336 16.145v-0.003906c-0.44922 0.41016-0.59766 1.0547-0.375 1.6211 0.21875 0.5625 0.76562 0.93359 1.3711 0.93359h35.609c0.60547 0 1.1523-0.37109 1.3711-0.93359 0.22266-0.56641 0.074219-1.2109-0.375-1.6211l-17.324-16.133v-5.8867c6.2188-4.8594 11.719-10.215 16.176-16.375l7.207-2.5234c8.5625-2.9922 15.031-10.117 17.164-18.934l4.9922-20.668 0.003906 0.003906c0.10547-0.4375 0.003907-0.89844-0.27344-1.2539-0.27734-0.35156-0.69922-0.55859-1.1484-0.5625h-15.914c-0.10156-2.2617-0.28125-4.5898-0.54688-6.9922-0.085938-0.74219-0.71484-1.3047-1.4648-1.3008zm1.4648 2.9414h52.426c3.0352 31.043-8.1797 48.887-26.215 63.09-18.039-14.203-29.246-32.047-26.211-63.09zm-17.52 8.2969h13.973c-0.20312 15.059 3.3125 27.004 9.3984 36.926l-3.5234-1.2305c-7.6211-2.6641-13.375-9.0039-15.273-16.848zm73.5 0h13.961l-4.5703 18.844c-1.8984 7.8477-7.6406 14.188-15.266 16.848l-3.5234 1.2305c6.0859-9.9219 9.6016-21.867 9.3984-36.926zm-29.773 65.895 14.082 13.102h-28.152z"/>
                </svg>
              </LeaderboardIconButton>
              <IconButton onClick={() => navigate('/graph')} title="Knowledge Graph">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
                  <path d="m89.34 62.84c-2.0508 0-3.8906 0.87109-5.1992 2.25l-18.551-11.25c0.57812-1.3398 0.91016-2.8086 0.91016-4.3594 0-3.4219-1.5703-6.4688-4.0195-8.4883l9.0703-14.801c1.0703 0.42969 2.2305 0.67969 3.4492 0.67969 5.0586 0 9.1914-4.1211 9.1914-9.1914 0-5.0703-4.1211-9.1914-9.1914-9.1914s-8.9492 3.8984-9.1602 8.7812c0 0.14062-0.019532 0.26953-0.019532 0.41016 0 0.21875 0.019532 0.42969 0.03125 0.64062l-10.828 2.5898c-0.98047-2.1484-2.8984-3.7695-5.2305-4.3594-0.60938-0.14844-1.25-0.23828-1.9102-0.23828-0.41016 0-0.82031 0.039062-1.2188 0.10156-2.5391 0.39844-4.6797 2.0117-5.7891 4.2305l-14.5-4.0391c0.050781-0.35938 0.078125-0.73047 0.10156-1.1016 0-0.17188 0.03125-0.33984 0.03125-0.51172 0-6.3398-5.1602-11.5-11.5-11.5-6.3477-0.003907-11.508 5.168-11.508 11.508s5.1602 11.5 11.5 11.5c2.9609 0 5.6602-1.1289 7.6992-2.9805l23.57 20.059c-0.75 1.1719-1.2812 2.4883-1.5391 3.8984l-15.109-0.69141c-0.55859-3.3906-3.5117-5.9805-7.0508-5.9805-3.9492 0-7.1602 3.2109-7.1602 7.1602 0 3.9492 3.2109 7.1602 7.1602 7.1602 3.3203 0 6.1094-2.2695 6.9102-5.3398l15.109 0.69141c0.23047 2.5508 1.3398 4.8516 3.0195 6.6016l-10.43 13.211c-2.1914-1.3906-4.7812-2.1992-7.5586-2.1992-7.8281 0-14.211 6.3711-14.211 14.211 0 7.8398 6.3711 14.211 14.211 14.211s14.211-6.3711 14.211-14.211c0-3.9688-1.6406-7.5703-4.2812-10.148l10.43-13.211c1.6289 0.96094 3.5312 1.5312 5.5586 1.5312 0.75 0 1.4883-0.078125 2.1992-0.21875l3.9492 11.23c-1.5898 1.0703-2.6406 2.8906-2.6406 4.9492 0 3.2891 2.6797 5.9688 5.9688 5.9688s5.9688-2.6797 5.9688-5.9688c0-3.2891-2.6797-5.9688-5.9688-5.9688-0.17188 0-0.33984 0.011718-0.5 0.03125l-3.9609-11.238c1.3516-0.69922 2.5391-1.6602 3.4883-2.8281l18.551 11.25c-0.26172 0.73828-0.39844 1.5195-0.39844 2.3398 0 3.9492 3.2109 7.1602 7.1602 7.1602s7.1602-3.2109 7.1602-7.1602-3.2109-7.1602-7.1602-7.1602zm-4.1602 7.1602c0-2.2891 1.8594-4.1602 4.1602-4.1602 2.3008 0 4.1602 1.8594 4.1602 4.1602s-1.8711 4.1602-4.1602 4.1602c-2.2891 0-4.1602-1.8594-4.1602-4.1602zm-37.648-20.52c0-4.3984 3.5781-7.9805 7.9805-7.9805 4.3984 0 7.9805 3.5781 7.9805 7.9805 0 4.3984-3.5781 7.9805-7.9805 7.9805-4.3984 0-7.9805-3.5781-7.9805-7.9805zm-32.531-25.98c-4.6914 0-8.5-3.8086-8.5-8.5s3.8086-8.5 8.5-8.5 8.5 3.8086 8.5 8.5-3.8086 8.5-8.5 8.5zm10.578-4 14.488 4.0391c-0.019531 0.21094-0.03125 0.41016-0.03125 0.62109 0 4.3281 3.5195 7.8516 7.8516 7.8516 4.3281 0 7.8516-3.5195 7.8516-7.8516 0-0.10938-0.011719-0.21875-0.019531-0.32812l10.82-2.5781c0.55078 1.3086 1.3984 2.4492 2.4609 3.3711l-9.0703 14.809c-1.3516-0.58984-2.8398-0.92969-4.3984-0.92969-2.8008 0-5.3594 1.0703-7.3008 2.8008l-23.582-20.066c0.35937-0.55078 0.67187-1.1289 0.92969-1.7383zm22.301-0.19141c2.6719 0 4.8516 2.1797 4.8516 4.8516s-2.1719 4.8516-4.8516 4.8516c-2.6797 0-4.8516-2.1797-4.8516-4.8516s2.1797-4.8516 4.8516-4.8516zm20.93-1.6289c0-3.4102 2.7695-6.1914 6.1914-6.1914s6.1914 2.7695 6.1914 6.1914c0 3.4219-2.7695 6.1914-6.1914 6.1914s-6.1914-2.7695-6.1914-6.1914zm-50.41 30.289c0-2.2891 1.8711-4.1602 4.1602-4.1602 2.2891 0 4.1602 1.8594 4.1602 4.1602s-1.8711 4.1602-4.1602 4.1602c-2.2891 0-4.1602-1.8594-4.1602-4.1602zm0 34.32c0-6.1797 5.0312-11.211 11.211-11.211s11.211 5.0312 11.211 11.211c0 6.1797-5.0312 11.211-11.211 11.211s-11.211-5.0312-11.211-11.211zm49.559-5.8711c0 1.6406-1.3281 2.9688-2.9688 2.9688s-2.9688-1.3281-2.9688-2.9688 1.3281-2.9688 2.9688-2.9688 2.9688 1.3281 2.9688 2.9688z"/>
                </svg>
              </IconButton>
              
              {/* Mobile Right Sidebar Toggle for small screens */}
              <MobileMenuButton 
                onClick={() => setRightSidebarOpen(!rightSidebarOpen)}
                title="Your Journey"
                style={{ display: 'none' }}
              >
                <svg viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM7.07 18.28c.43-.9 3.05-1.78 4.93-1.78s4.51.88 4.93 1.78C15.57 19.36 13.86 20 12 20s-3.57-.64-4.93-1.72zm11.29-1.45c-1.43-1.74-4.9-2.33-6.36-2.33s-4.93.59-6.36 2.33C4.62 15.49 4 13.82 4 12c0-4.41 3.59-8 8-8s8 3.59 8 8c0 1.82-.62 3.49-1.64 4.83z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
              </MobileMenuButton>
            </>
          )}
        </NavigationButtons>
      </TopBar>
      
      {/* Main Content Area */}
      <MainContent hasHeaderSearch={!!(llmResponse || isLoading)} rightSidebarOpen={rightSidebarOpen} hasNotice={showNotice}>
        {!llmResponse && !isLoading ? (
          <SearchSection>
            <WelcomeTitle>What fandom mystery are you exploring today?</WelcomeTitle>
            <SearchBox onSearch={handleSearch} isLoading={isLoading} />
            
            {/* Mobile Recommendations - only show on mobile */}
            <MobileRecommendations>
              <PatternBasedRecommendations
                userId={user?.id}
                onSearchRecommendation={handleSearch}
                compact={false}
                limit={6}
              />
            </MobileRecommendations>
          </SearchSection>
        ) : (
          <ContentArea>
            {isLoading && (
              <EnhancedLoadingStatus 
                status="Searching..."
                query={currentQuery}
                memorySummary={memorySummary}
              />
            )}
            
            {llmResponse && !isLoading && (
              <ResultsContainer>
                {/* Mobile Query Context Bar */}
                {currentQuery && (
                  <QueryContextBar>
                    <QueryLabel>Currently Exploring</QueryLabel>
                    <QueryText>"{currentQuery}"</QueryText>
                  </QueryContextBar>
                )}
                
                <OverviewSection>
                  <MarkdownContent content={llmResponse.overview} disableLinks={true} />
                </OverviewSection>
                
                {llmResponse.concepts.length > 0 && (
                  <>
                    <SectionHeading>Explore Deeper</SectionHeading>
                    <ConceptGrid columns={Math.min(llmResponse.concepts.length, 2)}>
                      {llmResponse.concepts.map((concept, index) => (
                        <AdvancedConceptCard 
                          key={index} 
                          concept={concept}
                          parentQuery={currentQuery}
                          onExplore={(query) => {
                            setClickedConceptIndex(index);
                            handleSearch(query);
                          }}
                          isClicked={clickedConceptIndex === index}
                          isLoading={isLoading && clickedConceptIndex === index}
                        />
                      ))}
                    </ConceptGrid>
                  </>
                )}
                
                {/* Sources Panel in main content for results */}
                {searchResults.length > 0 && (
                  <SourcesPanel sources={searchResults} loading={isLoading} />
                )}
              </ResultsContainer>
            )}
          </ContentArea>
        )}
      </MainContent>
      
      {/* Modal Overlays */}
      {showLeaderboard && (
        <Modal>
          <ModalBackdrop onClick={() => setShowLeaderboard(false)} />
          <ModalContent>
            <Leaderboard onClose={() => setShowLeaderboard(false)} />
          </ModalContent>
        </Modal>
      )}
      
      {needsUsername && (
        <UsernameSetup onComplete={() => setNeedsUsername(false)} />
      )}
      
      {/* Rate Limit Toast */}
      {rateLimitToast && (
        <RateLimitToast
          message={rateLimitToast.message}
          remainingTime={rateLimitToast.remainingTime}
          onClose={() => setRateLimitToast(null)}
        />
      )}
      </AppContainer>
    </>
  );
};